if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
      }

      connectedCallback() {
        try {
          this.bindListeners();

          if (this.dataset.filterByVariantAlt === 'true') {
            const selectedOptions = this.dataset.selectedVariantOptions ? this.dataset.selectedVariantOptions.split('||').map(o => o.trim().toLowerCase()) : [];
            this.filterMedia(selectedOptions);

            // Force execution on next tick to sync with fully upgraded custom elements on reload
            setTimeout(() => {
              try {
                this.bindListeners();
                this.filterMedia(selectedOptions);
              } catch (err) {
                console.error('Error in connectedCallback timeout:', err);
              }
            }, 1);

            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                try {
                  if (event.data && event.data.variant) {
                    const opts = event.data.variant.options.map(o => o.trim().toLowerCase());
                    this.bindListeners();
                    this.filterMedia(opts);
                  }
                } catch (err) {
                  console.error('Error in variantChange subscriber:', err);
                }
              });
            } else {
              console.warn('pubsub.js or constants.js not loaded yet');
            }
          }
        } catch (err) {
          console.error('Error in connectedCallback:', err);
        }
      }

      bindListeners() {
        if (!this.elements) {
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          };
        } else {
          if (!this.elements.viewer) this.elements.viewer = this.querySelector('[id^="GalleryViewer"]');
          if (!this.elements.thumbnails) this.elements.thumbnails = this.querySelector('[id^="GalleryThumbnails"]');
        }

        if (this.elements.viewer && this.elements.thumbnails && !this.elements.thumbnails._listenersBound) {
          this.elements.thumbnails._listenersBound = true;
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        if (!this.elements || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      filterMedia(selectedOptions) {
        if (this.dataset.filterByVariantAlt !== 'true') return;

        this.bindListeners();
        if (!this.elements.viewer) return;

        const optionValues = this.dataset.allOptionValues ? this.dataset.allOptionValues.split('||').filter(Boolean) : [];
        const slides = Array.from(this.elements.viewer.querySelectorAll('ul[id^="Slider-Gallery-"] > li[id^="Slide-"]'));
        const thumbnails = this.elements.thumbnails ? Array.from(this.elements.thumbnails.querySelectorAll('ul[id^="Slider-Thumbnails-"] > li[id^="Slide-Thumbnails-"]')) : [];
        const dots = Array.from(this.elements.viewer.querySelectorAll('.slider-dots-container .slider-dot'));
        
        const sectionId = this.id.replace('MediaGallery-', '');
        const modal = document.getElementById(`ProductModal-${sectionId}`);
        const modalItems = modal ? Array.from(modal.querySelectorAll('.product-media-modal__content > *')) : [];

        const matchesOptionValue = (alt, value) => {
          if (!alt || !value) return false;
          const altParts = alt.split('|').map(p => p.trim().toLowerCase());
          const cleanVal = value.trim().toLowerCase();
          return altParts.includes(cleanVal);
        };

        const matchesSelectedOptions = (alt) => {
          if (!alt) return false;
          return selectedOptions.some(val => matchesOptionValue(alt, val));
        };

        const isDefault = optionValues.length === 0 || (selectedOptions.length === 1 && selectedOptions[0] === 'default title');
        let activeMediaSet = false;

        // 1. Filter slides and determine which slide is active
        slides.forEach(slide => {
          const alt = slide.getAttribute('data-alt') || '';
          const shouldShow = isDefault || matchesSelectedOptions(alt);

          if (shouldShow) {
            slide.style.removeProperty('display');
            slide.removeAttribute('style');
            if (!activeMediaSet) {
              slide.classList.add('is-active');
              activeMediaSet = true;
            } else {
              slide.classList.remove('is-active');
            }
          } else {
            slide.style.setProperty('display', 'none', 'important');
            slide.classList.remove('is-active');
          }
        });

        const activeSlide = slides.find(s => s.classList.contains('is-active') && s.style.getPropertyValue('display') !== 'none');
        const activeMediaId = activeSlide ? activeSlide.dataset.mediaId : null;

        // 2. Filter thumbnails based on target matching and active media
        thumbnails.forEach(thumbnail => {
          const alt = thumbnail.getAttribute('data-alt') || '';
          const shouldShow = isDefault || matchesSelectedOptions(alt);
          if (shouldShow) {
            thumbnail.style.removeProperty('display');
            thumbnail.removeAttribute('style');
            if (activeMediaId && thumbnail.dataset.target === activeMediaId) {
              thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
            } else {
              thumbnail.querySelector('button')?.removeAttribute('aria-current');
            }
          } else {
            thumbnail.style.setProperty('display', 'none', 'important');
            thumbnail.querySelector('button')?.removeAttribute('aria-current');
          }
        });

        // 3. Filter dots based on target matching and active media
        dots.forEach(dot => {
          const target = dot.getAttribute('data-target');
          const slide = slides.find(s => s.dataset.mediaId === target);
          const shouldShow = slide && slide.style.getPropertyValue('display') !== 'none';
          if (shouldShow) {
            dot.style.removeProperty('display');
            if (activeMediaId && target === activeMediaId) {
              dot.classList.add('is-active');
            } else {
              dot.classList.remove('is-active');
            }
          } else {
            dot.style.setProperty('display', 'none', 'important');
            dot.classList.remove('is-active');
          }
        });

        // 4. Filter items in the modal popup
        modalItems.forEach((item) => {
          const alt = item.getAttribute('data-alt') || '';
          const shouldShow = isDefault || matchesSelectedOptions(alt);
          if (shouldShow) {
            item.style.removeProperty('display');
            item.removeAttribute('style');
          } else {
            item.style.setProperty('display', 'none', 'important');
            item.classList.remove('active');
          }
        });

        // 5. Hide slider buttons / counters if less than 2 slides visible
        const visibleSlides = slides.filter(s => s.style.getPropertyValue('display') !== 'none');
        const visibleIndex = visibleSlides.length;

        const prevButton = this.elements.viewer.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');
        const sliderCounter = this.elements.viewer.querySelector('.slider-counter');
        const sliderDots = this.elements.viewer.querySelector('.slider-dots-container');

        if (visibleIndex < 2) {
          if (prevButton) prevButton.style.setProperty('display', 'none', 'important');
          if (nextButton) nextButton.style.setProperty('display', 'none', 'important');
          if (sliderCounter) sliderCounter.style.setProperty('display', 'none', 'important');
          if (sliderDots) sliderDots.style.setProperty('display', 'none', 'important');
        } else {
          if (prevButton) prevButton.style.removeProperty('display');
          if (nextButton) nextButton.style.removeProperty('display');
          if (sliderCounter) sliderCounter.style.removeProperty('display');
          if (sliderDots) sliderDots.style.removeProperty('display');
        }

        // Ensure browser layout reflow has completed before recalculating slider pages and scrolling to active elements
        requestAnimationFrame(() => {
          if (this.elements.viewer && this.elements.viewer.resetPages) {
            this.elements.viewer.resetPages();
          }
          if (this.elements.thumbnails && this.elements.thumbnails.resetPages) {
            this.elements.thumbnails.resetPages();
          }

          // Scroll main slider horizontally to the active slide
          if (activeSlide && activeSlide.parentElement) {
            activeSlide.parentElement.scrollTo({ left: activeSlide.offsetLeft });
          }

          // Scroll thumbnail slider horizontally to the active thumbnail
          const activeThumbnail = thumbnails.find(t => t.dataset.target === activeMediaId && t.style.getPropertyValue('display') !== 'none');
          if (activeThumbnail && this.elements.thumbnails && this.elements.thumbnails.slider) {
            this.elements.thumbnails.slider.scrollTo({ left: activeThumbnail.offsetLeft });
          }
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          const activeDot = this.elements.viewer.querySelector(`.slider-dot[data-target="${mediaId}"]`);
          if (activeDot) {
            activeDot.parentElement.firstChild !== activeDot && activeDot.parentElement.prepend(activeDot);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
